// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
// --- Components
import { DetailsModal } from "tap/components/DetailsModal/index.jsx";
import { JobViewModalHeader } from "tap/components/JobViewModalHeader/index.jsx";
import { PaginationBar } from "tap/components/PaginationBar/index.jsx";
import { JobViewGrid } from "./grid";
// --- Hooks & Actions
import { useJobsData } from "../../../hooks/useJobsData";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { useStoredModalItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
// --- Utils
import { tapApi } from "tap/api.js";
// --- Styles
import "./styles.scss";

/**
 * Job Status Modal (Job View)
 *
 * Features:
 * - Displays a modal with job status/view details
 * - Supports pagination to navigate through job tasks
 * - Fetches job and task data from API
 *
 * @returns {JSX.Element}
 */
export const JobStatusModal = () => {
  const {
    isJobViewModalOpen,
    jobViewTotalPages,
    jobViewCurrentPage,
    jobViewTotalItems,
    jobViewItemsPerPage,
    selectedJob,
    jobViewFilterForm,
    jobViewTasks,
    pendingStatuses,
  } = useSelector((state) => state.tapJobs);

  const { fetchJobs, updateJobs, tapJobRange } = useJobsData();
  const { clearJobViewModal } = useJobsActions();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();

  useStoredModalItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.tapJobsJobView,
    isOpen: isJobViewModalOpen,
    itemsPerPage: jobViewItemsPerPage,
    onSync: updateJobs,
    itemsPerPageField: "jobViewItemsPerPage",
    currentPageField: "jobViewCurrentPage",
  });

  // Close modal and refresh the jobs list
  const onCloseModal = async () => {
    clearJobViewModal();
    const data = await fetchJobs(tapJobRange, null, null, true);
    if (data) {
      updateJobs({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  const handlePageChange = async (pageNumber) => {
    setLoadingTrue();
    try {
      const apiPage = pageNumber + 1;
      const job = await tapApi.job(selectedJob?.id, false);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        false,
        apiPage,
        jobViewItemsPerPage,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateJobs({
        selectedJob: job,
        jobViewTasks: data?.tasks,
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements,
        jobViewCurrentPage: pageNumber,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
      console.error("Error fetching job tasks:", error);
    } finally {
      setLoadingFalse();
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setLoadingTrue();
    try {
      const job = await tapApi.job(selectedJob?.id, false);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        false,
        1,
        newSize,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateJobs({
        selectedJob: job,
        jobViewTasks: data?.tasks,
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements,
        jobViewCurrentPage: 0,
        jobViewItemsPerPage: newSize,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
      console.error("Error fetching job tasks:", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <DetailsModal
      modalHeader={
        <JobViewModalHeader
          selectedJob={selectedJob}
          jobTasks={jobViewTasks}
          pendingStatuses={pendingStatuses}
        />
      }
      isOpen={isJobViewModalOpen}
      onClose={onCloseModal}
      footer={
        <div className="job-status-modal-footer">
          <PaginationBar
            totalPages={jobViewTotalPages}
            currentPage={jobViewCurrentPage}
            totalItems={jobViewTotalItems}
            itemsPerPage={jobViewItemsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      }
    >
      <JobViewGrid />
    </DetailsModal>
  );
};
